import { WritableSignal, signal } from '@angular/core';
import {
    SealedBid,
    SealedLot,
    SealedLotActive,
    SealedLotFinished,
    SealedLotState,
} from '../domain/models';

export interface SealedLotRef {
    readonly id: number;
    readonly model: WritableSignal<SealedLotState>;
}

export class SealedLotStore {
    private readonly lotRefs = new Map<number, SealedLotRef>();

    get refsByLotId() {
        return this.lotRefs;
    }

    upsertLots(lots: readonly SealedLot[], startDate: Date, endDate: Date): void {
        for (const lot of lots) {
            if (this.lotRefs.has(lot.id)) {
                continue;
            }

            const initialState: SealedLotActive = {
                kind: 'Active',
                lot,
                bidCount: 0,
                myBid: null,
                startedAt: startDate,
                endsAt: endDate,
            };

            this.lotRefs.set(lot.id, {
                id: lot.id,
                model: signal<SealedLotState>(initialState),
            });
        }
    }

    setBidCount(lotId: number, bidCount: number): void {
        const ref = this.lotRefs.get(lotId);
        if (!ref) return;

        ref.model.update((state) => ({ ...state, bidCount }));
    }

    setMyBid(lotId: number, bid: SealedBid): void {
        const ref = this.lotRefs.get(lotId);
        if (!ref) return;

        ref.model.update((state) => {
            if (state.kind === 'Finished') {
                return state;
            }

            return { ...state, myBid: bid };
        });
    }

    removeMyBid(lotId: number): void {
        const ref = this.lotRefs.get(lotId);
        if (!ref) return;

        ref.model.update((state) => {
            if (state.kind === 'Finished') {
                return state;
            }
            return { ...state, myBid: null };
        });
    }

    activateLot(payload: { lotId: number; bidCount: number; startedAt: Date; endsAt?: Date }): void {
        const ref = this.lotRefs.get(payload.lotId);
        if (!ref) return;

        const current = ref.model();
        if (current.kind === 'Finished') {
            return;
        }

        const nextState: SealedLotActive = {
            kind: 'Active',
            lot: current.lot,
            bidCount: payload.bidCount,
            myBid: current.myBid,
            startedAt: payload.startedAt,
            endsAt: payload.endsAt ?? current.endsAt,
        };

        ref.model.set(nextState);
    }

    finishLot(payload: {
        lotId: number;
        bidCount: number;
        finishedAt?: Date;
        leadingSealedBidId: number | null;
    }): void {
        const ref = this.lotRefs.get(payload.lotId);
        if (!ref) {
            return;
        }

        const current = ref.model();
        if (current.kind === 'Finished') {
            return;
        }

        const nextState: SealedLotFinished = {
            kind: 'Finished',
            lot: current.lot,
            bidCount: payload.bidCount,
            myBid: current.myBid,
            finishedAt: payload.finishedAt ?? new Date(),
            leadingSealedBidId: payload.leadingSealedBidId,
        };

        ref.model.set(nextState);
    }


    clear(): void {
        this.lotRefs.clear();
    }
}

